import { Container } from 'hypo-container';

import { DataMapper, HttpTransport } from '@estee/elc-http-transport';
import { RouterStore } from '@estee/elc-router';
import { getDecorators } from '@estee/elc-service';
import { IAppConfig, root } from '@estee/elc-universal-utils';

import apiDataMapping from '~api/DataMappings';
import { RampPgApiSdk } from '~api/RampPgApiSdk';
import { DropDownViewController } from '~components/DropDownViewController';
import { LanguageSwitcherController } from '~components/LanguageSwitcherController';
import { IRampAppConfig } from '~domain/entities/Config';
import { Password } from '~domain/entities/Password';
import { RampSignInSignUpViewController } from '~exported/ramp-sign-in-sign-up/RampSignInSignUpViewController';
import { RampViewController } from '~exported/ramp/RampViewController';
import { ApiDropDownRepository } from '~repositories/ApiDropDownRepository';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { DropdownDataStore } from '~repositories/DropdownDataStore';
import { MessageRepository } from '~repositories/MessageRepository';
import { PasswordRepository } from '~repositories/PasswordRepository';
import { TranslationRepository } from '~repositories/TranslationRepository';
import { UserRepository } from '~repositories/UserRepository';
import { NOT_FOUND_ROUTE, ROUTES } from '~routes/routes';
import { RampServiceStartedUseCase } from '~use-cases/RampServiceStartedUseCase';
import { AccountAccessPageViewController } from '~views/account/AccountAccessPageViewController';
import { AccountSignInFormViewController } from '~views/account/AccountSignInFormViewController';
import { ConsentFormViewController } from '~views/account/ConsentFormViewController';
import { DynamicFormViewController } from '~views/common/DynamicFormViewController';
import { UserSessionSuspenseViewController } from '~views/common/UserSessionSuspenseViewController';
import { ApiDropDownViewController } from '~views/components/ApiDropDownViewController';
import { PasswordCriteriaViewController } from '~views/components/PasswordCriteriaViewController';
import { PasswordStrengthViewController } from '~views/components/PasswordStrengthViewController';
import { PasswordViewController } from '~views/components/PasswordViewController';
import { SocialButtonWrapperViewController } from '~views/components/SocialButtonWrapperViewController';
import { EmailFormViewController } from '~views/email/EmailFormViewController';
import { GuestCheckoutFormViewController } from '~views/guest/GuestCheckoutFormViewController';
import { OtpEmailViewController } from '~views/otp/OtpEmailViewController';
import { OtpSmsViewController } from '~views/otp/OtpSmsViewController';
import { OtpStateHelper } from '~views/otp/OtpStateHelper';
import { IOtpViewController } from '~views/otp/OtpViewController';
import { QRCodeViewController } from '~views/qrcode/QRCodeViewController';
import { RampMainViewController } from '~views/RampMainViewController';
import { RedirectViewController } from '~views/redirect/RedirectViewController';
import { ResetPasswordFormViewController } from '~views/resetPassword/ResetPasswordFormViewController';
import { SignUpViewController } from '~views/signup/SignUpViewController';
import { RegisterSuccessViewController } from '~views/social-sign-up/RegisterSuccessViewController';

import serviceNames, { RampService } from './RampService';

export const diContainer = new Container();

export const setConfig = (configJSON: IRampAppConfig) => {
    const { businessUnitId, pgApiTokens } = configJSON;
    diContainer.config = { businessUnitId, pgApiTokens };
};

export async function serviceInitializer(config: IAppConfig) {
    setConfig(config);

    const routerStore = diContainer.get(serviceNames.routerStore);
    // call startRouting early to make sure components could access queryParams
    routerStore?.startRouting();
    await new RampServiceStartedUseCase().execute();
}

export const getParamsForOtpViewController = (c: Container): IOtpViewController => ({
    otpStateHelper: c.get(serviceNames.otpStateHelper),
    userRepository: c.get(serviceNames.userRepository),
    configRepository: c.get(serviceNames.configRepository),
    messageRepository: c.get(serviceNames.messageRepository),
    routerStore: c.get(serviceNames.routerStore)
});

diContainer
    .register(serviceNames.apiDataMapper, () => new DataMapper(apiDataMapping))
    .register(serviceNames.httpTransport, () => {
        return root.HttpTransport;
    })
    .register(serviceNames.rampService, (c) => new RampService(serviceInitializer))
    .register(
        serviceNames.apiDropDownViewController,
        diContainer.factory(
            (c) =>
                new ApiDropDownViewController({
                    apiDropDownRepository: c.get(serviceNames.apiDropDownRepository)
                })
        )
    )
    .register(serviceNames.routerStore, () => new RouterStore(ROUTES, NOT_FOUND_ROUTE))
    .register(serviceNames.configRepository, (c) => new ConfigRepository(c.config))
    .register(serviceNames.translationRepository, (c) => new TranslationRepository())
    .register(serviceNames.dropdownDataStore, () => new DropdownDataStore())
    .register(
        serviceNames.dropDownViewController,
        diContainer.factory(
            (c) =>
                new DropDownViewController({
                    dropdownDataStore: c.get(serviceNames.dropdownDataStore),
                    configRepository: c.get(serviceNames.configRepository)
                })
        )
    )
    .register(
        serviceNames.dynamicFormViewController,
        diContainer.factory(
            (c) =>
                new DynamicFormViewController({
                    configRepository: c.get(serviceNames.configRepository),
                    translationRepository: c.get(serviceNames.translationRepository)
                })
        )
    )
    .register(
        serviceNames.guestCheckoutFormViewController,
        (c) =>
            new GuestCheckoutFormViewController({
                userRepository: c.get(serviceNames.userRepository),
                messageRepository: c.get(serviceNames.messageRepository)
            })
    )
    .register(serviceNames.apiDropDownRepository, () => new ApiDropDownRepository())
    .register(serviceNames.passwordRepository, () => new PasswordRepository())
    .register(serviceNames.passwordEntity, () => new Password())
    .register(serviceNames.rampPgApiSdk, (c) => {
        return new RampPgApiSdk(new HttpTransport(), c.get(serviceNames.apiDataMapper));
    })
    .register(serviceNames.userRepository, () => {
        return new UserRepository();
    })
    .register(serviceNames.messageRepository, () => {
        return new MessageRepository();
    })
    .register(
        serviceNames.signUpViewController,
        (c) =>
            new SignUpViewController({
                userRepository: c.get(serviceNames.userRepository),
                configRepository: c.get(serviceNames.configRepository),
                messageRepository: c.get(serviceNames.messageRepository)
            })
    )
    .register(
        serviceNames.rampViewController,
        diContainer.factory(
            (c) =>
                new RampViewController({
                    routerStore: c.get(serviceNames.routerStore),
                    userRepository: c.get(serviceNames.userRepository),
                    translationRepository: c.get(serviceNames.translationRepository),
                    configRepository: c.get(serviceNames.configRepository)
                })
        )
    )
    .register(
        serviceNames.rampSignInSignUpViewController,
        diContainer.factory(
            (c) =>
                new RampSignInSignUpViewController({
                    routerStore: c.get(serviceNames.routerStore),
                    configRepository: c.get(serviceNames.configRepository),
                    userRepository: c.get(serviceNames.userRepository)
                })
        )
    )
    .register(
        serviceNames.rampMainViewController,
        diContainer.factory(
            (c) =>
                new RampMainViewController({
                    routerStore: c.get(serviceNames.routerStore),
                    configRepository: c.get(serviceNames.configRepository)
                })
        )
    )
    .register(serviceNames.redirectViewController, (c) => {
        return new RedirectViewController({
            configRepository: c.get(serviceNames.configRepository),
            routerStore: c.get(serviceNames.routerStore)
        });
    })
    .register(
        serviceNames.accountAccessViewController,
        (c) =>
            new AccountAccessPageViewController({
                userRepository: c.get(serviceNames.userRepository),
                configRepository: c.get(serviceNames.configRepository),
                routerStore: c.get(serviceNames.routerStore)
            })
    )
    .register(
        serviceNames.accountSignInFormViewController,
        (c) =>
            new AccountSignInFormViewController({
                userRepository: c.get(serviceNames.userRepository),
                configRepository: c.get(serviceNames.configRepository),
                messageRepository: c.get(serviceNames.messageRepository)
            })
    )
    .register(
        serviceNames.emailFormViewController,
        (c) =>
            new EmailFormViewController({
                userRepository: c.get(serviceNames.userRepository),
                configRepository: c.get(serviceNames.configRepository)
            })
    )
    .register(
        serviceNames.resetPasswordFormViewController,
        (c) =>
            new ResetPasswordFormViewController({
                userRepository: c.get(serviceNames.userRepository),
                configRepository: c.get(serviceNames.configRepository),
                routerStore: c.get(serviceNames.routerStore)
            })
    )
    .register(
        serviceNames.qrCodeViewController,
        (c) =>
            new QRCodeViewController({
                userRepository: c.get(serviceNames.userRepository),
                configRepository: c.get(serviceNames.configRepository),
                translationRepository: c.get(serviceNames.translationRepository),
                messageRepository: c.get(serviceNames.messageRepository)
            })
    )
    .register(
        serviceNames.socialButtonViewController,
        diContainer.factory((c) => {
            return new SocialButtonWrapperViewController({
                configRepository: c.get(serviceNames.configRepository),
                userRepository: c.get(serviceNames.userRepository)
            });
        })
    )
    .register(
        serviceNames.passwordViewController,
        diContainer.factory((c) => {
            return new PasswordViewController({
                passwordRepository: c.get(serviceNames.passwordRepository)
            });
        })
    )
    .register(
        serviceNames.passwordStrengthViewController,
        diContainer.factory(
            (c) =>
                new PasswordStrengthViewController({
                    passwordRepository: c.get(serviceNames.passwordRepository),
                    passwordEntity: c.get(serviceNames.passwordEntity)
                })
        )
    )
    .register(
        serviceNames.passwordCriteriaViewController,
        diContainer.factory(
            (c) =>
                new PasswordCriteriaViewController({
                    passwordRepository: c.get(serviceNames.passwordRepository),
                    translationRepository: c.get(serviceNames.translationRepository)
                })
        )
    )
    .register(
        serviceNames.consentFormViewController,
        (c) =>
            new ConsentFormViewController({
                translationRepository: c.get(serviceNames.translationRepository),
                userRepository: c.get(serviceNames.userRepository)
            })
    )
    .register(
        serviceNames.otpStateHelper,
        diContainer.factory(() => new OtpStateHelper())
    )
    .register(
        serviceNames.otpEmailViewController,
        diContainer.factory((c) => new OtpEmailViewController(getParamsForOtpViewController(c)))
    )
    .register(
        serviceNames.otpSmsViewController,
        diContainer.factory((c) => new OtpSmsViewController(getParamsForOtpViewController(c)))
    )
    .register(
        serviceNames.registerSuccessViewController,
        diContainer.factory(
            (c) =>
                new RegisterSuccessViewController({
                    configRepository: c.get(serviceNames.configRepository),
                    translationRepository: c.get(serviceNames.translationRepository)
                })
        )
    )
    .register(
        serviceNames.userSessionSuspenseViewController,
        diContainer.factory(
            (c) =>
                new UserSessionSuspenseViewController({
                    userRepository: c.get(serviceNames.userRepository)
                })
        )
    )
    .register(
        serviceNames.languageSwitcherController,
        diContainer.factory(
            (c) =>
                new LanguageSwitcherController({
                    dropdownDataStore: c.get(serviceNames.dropdownDataStore),
                    userRepository: c.get(serviceNames.userRepository)
                })
        )
    );

const { lazyInject } = getDecorators(diContainer);
export { lazyInject };
